import { commonComponents as cc } from "@/components/common";
import { useEntryWithDiaryQuery } from "@/features/entries/api/selectEntryWithDiary";
import { useDeleteEntryMutation } from "@/features/entries/api/deleteEntry";
import { useSchedulesQuery } from "@/features/schedules/api/selectSchedules";
import EditableEntryCardEntryValues from "@/features/entry-values/components/EditableEntryCardEntryValues";
import { supabaseClient } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import { services as s } from "@/services";
import { Button } from "@patient-watch/ui/button";
import { formComponents as fc } from "@/features/i18n/formComponents";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@patient-watch/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@patient-watch/ui/drawer";
import { Input } from "@patient-watch/ui/input";
import { Label } from "@patient-watch/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@patient-watch/ui/select";
import { ScrollArea } from "@patient-watch/ui/scroll-area";
import { toast } from "@patient-watch/ui/sonner";
import { useToastCopy } from "@/features/i18n/toastCopy";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertTriangle, Check, ExternalLink, Search, SortAsc, SortDesc } from "lucide-react";
import { useEffect, useState } from "react";
import Fuse from "fuse.js";
import type { ExpandedSchedule } from "@patient-watch/types";
import { formatScheduleSummary } from "@patient-watch/forms";

// Simple media query hook
function useMediaQuery(query: string): boolean {
    const [matches, setMatches] = useState(false);

    useEffect(() => {
        const media = window.matchMedia(query);
        if (media.matches !== matches) {
            setMatches(media.matches);
        }
        const listener = () => setMatches(media.matches);
        media.addEventListener("change", listener);
        return () => media.removeEventListener("change", listener);
    }, [matches, query]);

    return matches;
}

const TIME_UNITS = ["second", "minute", "hour", "day", "week", "month"] as const;

export const Route = createFileRoute("/_app/admin/entry/$entryId/")({
    component: AdminEntryEditor,
});

// Schedule list component for the dialog
interface ScheduleListProps {
    schedules: ExpandedSchedule[];
    scheduleId: string | null;
    scheduleSearch: string;
    setScheduleSearch: (search: string) => void;
    alphaAsc: boolean;
    setAlphaAsc: (asc: boolean) => void;
    onSelect: (id: string | null) => void;
}

function ScheduleList({
    schedules,
    scheduleId,
    scheduleSearch,
    setScheduleSearch,
    alphaAsc,
    setAlphaAsc,
    onSelect,
}: ScheduleListProps) {
    const sortLabel = `Sort: ${alphaAsc ? "A-Z" : "Z-A"}`;
    const sortTooltip = `Sort alphabetically (${alphaAsc ? "A-Z" : "Z-A"})`;

    return (
        <>
            <div className="flex items-center gap-2 mb-2">
                <div className="relative flex-1">
                    <input
                        type="text"
                        placeholder="Search schedules..."
                        value={scheduleSearch}
                        onChange={(e) => setScheduleSearch(e.target.value)}
                        aria-label="Search schedules"
                        className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full text-sm"
                    />
                    <Search className="top-1/2 right-2 absolute w-4 h-4 text-slate-500 -translate-y-1/2 pointer-events-none" />
                </div>
                <div className="flex items-center gap-1">
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setAlphaAsc(!alphaAsc)}
                        title={sortTooltip}
                        aria-label={sortTooltip}
                    >
                        {alphaAsc ? <SortAsc className="w-5 h-5" /> : <SortDesc className="w-5 h-5" />}
                    </Button>
                    <span className="min-w-[80px] text-slate-500 text-xs" title={sortTooltip}>
                        {sortLabel}
                    </span>
                </div>
            </div>
            <ScrollArea className="h-[50vh]">
                <div className="flex flex-col gap-2">
                    {/* No Schedule option */}
                    <button
                        type="button"
                        className={cn(
                            "flex flex-col gap-1 hover:bg-slate-50 p-3 border rounded-lg w-full text-left transition",
                            scheduleId === null
                                ? "border-indigo-600 ring-2 ring-indigo-100 bg-indigo-50/40"
                                : "border-slate-200"
                        )}
                        onClick={() => onSelect(null)}
                    >
                        <div className="flex justify-between items-center w-full">
                            <span className={cn("font-semibold text-sm", scheduleId === null && "text-indigo-700")}>
                                No Schedule
                            </span>
                            {scheduleId === null && <Check size={16} className="text-indigo-600" />}
                        </div>
                    </button>

                    {schedules.length === 0 && (
                        <div className="py-8 text-slate-400 text-center">No schedules found.</div>
                    )}

                    {schedules.map((schedule) => (
                        <button
                            key={schedule.schedule_id}
                            type="button"
                            className={cn(
                                "flex flex-col gap-1 hover:bg-slate-50 p-3 border rounded-lg w-full text-left transition",
                                scheduleId === schedule.schedule_id
                                    ? "border-indigo-600 ring-2 ring-indigo-100 bg-indigo-50/40"
                                    : "border-slate-200"
                            )}
                            onClick={() => onSelect(schedule.schedule_id)}
                        >
                            <div className="flex justify-between items-center w-full">
                                <span
                                    className={cn(
                                        "font-semibold text-sm",
                                        scheduleId === schedule.schedule_id && "text-indigo-700"
                                    )}
                                >
                                    {formatScheduleSummary(schedule)}
                                </span>
                                {scheduleId === schedule.schedule_id && <Check size={16} className="text-indigo-600" />}
                            </div>
                            {schedule.schedule_description && (
                                <span className="text-slate-500 text-xs">{schedule.schedule_description}</span>
                            )}
                        </button>
                    ))}
                </div>
            </ScrollArea>
        </>
    );
}

function AdminEntryEditor() {
    const toastCopy = useToastCopy();
    const { entryId } = Route.useParams();
    const { data } = useEntryWithDiaryQuery(entryId);
    const { data: schedulesData } = useSchedulesQuery();
    const schedules = schedulesData ?? [];
    const queryClient = useQueryClient();
    const isMobile = useMediaQuery("(max-width: 640px)");

    const entry = data?.entry;
    const diary = data?.diary;

    // Form state for entry attributes
    const [scheduleId, setScheduleId] = useState<string | null>(entry?.schedule_id ?? null);
    const [scheduleIndex, setScheduleIndex] = useState<number | null>(entry?.schedule_index ?? null);
    const [trackerId, setTrackerId] = useState<string | null>(entry?.tracker_id ?? null);
    const [timingLabel, setTimingLabel] = useState<string>(entry?.timing_label ?? "");
    const [timingInterval, setTimingInterval] = useState<number | null>(entry?.timing_interval ?? null);
    const [timeUnit, setTimeUnit] = useState<string | null>(entry?.time_unit ?? null);

    // Schedule dialog state
    const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
    const [scheduleSearch, setScheduleSearch] = useState("");
    const [alphaAsc, setAlphaAsc] = useState(true);

    // Fuzzy search for schedules
    const fuse = new Fuse(schedules, {
        keys: ["schedule_name", "schedule_description"],
        threshold: 0.4,
    });

    let filteredSchedules = scheduleSearch ? fuse.search(scheduleSearch).map((r) => r.item) : schedules;
    filteredSchedules = [...filteredSchedules].toSorted((a, b) =>
        alphaAsc ? a.schedule_name.localeCompare(b.schedule_name) : b.schedule_name.localeCompare(a.schedule_name)
    );

    const selectedSchedule = schedules.find((s) => s.schedule_id === scheduleId);

    const handleScheduleSelect = (id: string | null) => {
        setScheduleId(id);
        setScheduleDialogOpen(false);
    };

    // Update entry mutation
    const updateEntryMutation = useMutation({
        mutationKey: ["admin-update-entry", entryId],
        mutationFn: async (data: {
            entry_id: string;
            schedule_id?: string | null;
            schedule_index?: number | null;
            tracker_id?: string | null;
            timing_label?: string | null;
            timing_interval?: number | null;
            time_unit?: string | null;
        }) => {
            const { data: result, error } = await supabaseClient
                .from("entries")
                .update({
                    schedule_id: data.schedule_id,
                    schedule_index: data.schedule_index,
                    tracker_id: data.tracker_id,
                    timing_label: data.timing_label,
                    timing_interval: data.timing_interval,
                    time_unit: data.time_unit,
                })
                .eq("entry_id", data.entry_id)
                .select()
                .single();

            if (error) throw error;
            return result;
        },
        onSuccess: () => {
            toast.success(toastCopy.entryUpdatedSuccess);
            void queryClient.invalidateQueries({
                queryKey: ["entry-with-diary", { entryId }],
            });
            if (diary?.diary_id) {
                void queryClient.invalidateQueries({
                    queryKey: ["diary", { diaryId: diary.diary_id }],
                });
            }
        },
        onError: (error) => {
            toast.error(toastCopy.failedUpdateEntry(error.message));
        },
    });

    // Delete entry mutation
    const deleteEntryMutation = useDeleteEntryMutation(diary?.diary_id ?? "");

    const handleSave = () => {
        updateEntryMutation.mutate({
            entry_id: entryId,
            schedule_id: scheduleId,
            schedule_index: scheduleIndex,
            tracker_id: trackerId,
            timing_label: timingLabel || null,
            timing_interval: timingInterval,
            time_unit: timeUnit,
        });
    };

    const handleDelete = () => {
        if (window.confirm("Are you sure you want to delete this entry? This action cannot be undone.")) {
            deleteEntryMutation.mutate(
                { entry_id: entryId },
                {
                    onSuccess: () => {
                        window.close();
                    },
                }
            );
        }
    };

    if (!entry || !diary) {
        return (
            <div className="p-8">
                <cc.Title words="Entry Not Found" />
                <p className="text-slate-500">The entry could not be found or you don't have permission to view it.</p>
            </div>
        );
    }

    // Get trackers from the diary
    const trackers = diary.trackers_view ?? [];
    const currentTracker = trackers.find((t) => t.tracker_id === trackerId);

    return (
        <div className="space-y-6 mx-auto p-6 max-w-4xl">
            {/* Header */}
            <div className="flex justify-between items-start">
                <div>
                    <cc.Title words="Admin Entry Editor" />
                    <p className="text-slate-500 text-sm">Edit entry attributes and values</p>
                </div>
                <Link
                    to="/diaries/$diaryId/home/results"
                    params={{ diaryId: diary.diary_id }}
                    target="_blank"
                    className="flex items-center gap-1 text-indigo-600 text-sm hover:underline"
                >
                    View Diary <ExternalLink size={14} />
                </Link>
            </div>

            {/* Warning Banner */}
            <div className="flex items-start gap-3 bg-amber-50 p-4 border border-amber-200 rounded-lg">
                <AlertTriangle className="mt-0.5 w-5 h-5 text-amber-600" />
                <div>
                    <p className="font-medium text-amber-800 text-sm">Admin Edit Mode</p>
                    <p className="text-amber-700 text-xs">
                        Changes here will directly modify the database. Use with caution.
                    </p>
                </div>
            </div>

            {/* Entry Info */}
            <div className="bg-slate-50 p-4 rounded-lg">
                <h3 className="mb-4 font-semibold text-lg">Entry Info</h3>
                <div className="gap-4 grid grid-cols-2 text-sm">
                    <div>
                        <span className="text-slate-500">Entry ID:</span>{" "}
                        <code className="text-xs">{entry.entry_id}</code>
                    </div>
                    <div>
                        <span className="text-slate-500">Entry Time:</span>{" "}
                        {s.time.formatDateTime(entry.calculated_entry_time ?? entry.entry_time)}
                    </div>
                    <div>
                        <span className="text-slate-500">Diary:</span> {diary.diary_name}
                    </div>
                    <div>
                        <span className="text-slate-500">Entry schedule_id:</span>{" "}
                        <code className="text-xs">{entry.schedule_id ?? "null"}</code>
                        {entry.schedule_id && (
                            <span className="ml-1 text-slate-400">
                                (
                                {(() => {
                                    const s = schedules.find((s) => s.schedule_id === entry.schedule_id);
                                    return formatScheduleSummary(s as any) || "Unknown";
                                })()}
                                )
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Tracker Info */}
            {currentTracker && (
                <div className="bg-blue-50 p-4 border border-blue-200 rounded-lg">
                    <h3 className="mb-4 font-semibold text-lg">Tracker Info</h3>
                    <div className="gap-4 grid grid-cols-2 text-sm">
                        <div>
                            <span className="text-slate-500">Tracker Name:</span>{" "}
                            {currentTracker.form?.form_name ?? currentTracker.tracker_name ?? "Unknown"}
                        </div>
                        <div>
                            <span className="text-slate-500">Tracker ID:</span>{" "}
                            <code className="text-xs">{currentTracker.tracker_id}</code>
                        </div>
                        <div>
                            <span className="text-slate-500">Tracker schedule_id:</span>{" "}
                            <code className="text-xs">{currentTracker.schedule_id ?? "null"}</code>
                            {currentTracker.schedule_id && (
                                <span className="ml-1 text-slate-400">
                                    (
                                    {(() => {
                                        const s = schedules.find((s) => s.schedule_id === currentTracker.schedule_id);
                                        return formatScheduleSummary(s as any) || "Unknown";
                                    })()}
                                    )
                                </span>
                            )}
                        </div>
                        <div>
                            <span className="text-slate-500">Tracker back_up_schedule_id:</span>{" "}
                            <code className="text-xs">
                                {(
                                    currentTracker as {
                                        back_up_schedule_id?: string | null;
                                    }
                                ).back_up_schedule_id ?? "null"}
                            </code>
                            {(
                                currentTracker as {
                                    back_up_schedule_id?: string | null;
                                }
                            ).back_up_schedule_id && (
                                <span className="ml-1 text-slate-400">
                                    (
                                    {(() => {
                                        const s = schedules.find(
                                            (s) =>
                                                s.schedule_id ===
                                                (
                                                    currentTracker as {
                                                        back_up_schedule_id?: string | null;
                                                    }
                                                ).back_up_schedule_id
                                        );
                                        return formatScheduleSummary(s as any) || "Unknown";
                                    })()}
                                    )
                                </span>
                            )}
                        </div>
                        <div>
                            <span className="text-slate-500">Start Date:</span>{" "}
                            {currentTracker.start_date ? s.time.formatDate(currentTracker.start_date) : "Not set"}
                        </div>
                        <div>
                            <span className="text-slate-500">Reference Date:</span>{" "}
                            {currentTracker.reference_date
                                ? s.time.formatDate(currentTracker.reference_date)
                                : "Not set"}
                        </div>
                    </div>
                </div>
            )}

            {/* Entry Attributes Form */}
            <div className="space-y-4 bg-white shadow-sm p-6 border rounded-lg">
                <h3 className="font-semibold text-lg">Entry Attributes</h3>

                {/* Schedule Selection */}
                <div className="space-y-2">
                    <Label>Schedule</Label>
                    <Button
                        type="button"
                        variant="outline"
                        className={cn(
                            "justify-between bg-white px-3 rounded-md w-full h-12 text-sm text-left",
                            !selectedSchedule && "text-slate-500"
                        )}
                        onClick={() => setScheduleDialogOpen(true)}
                    >
                        <span className="text-sm truncate">
                            {selectedSchedule ? (
                                <span className="font-medium">{formatScheduleSummary(selectedSchedule)}</span>
                            ) : (
                                "Select a schedule"
                            )}
                        </span>
                        <Search className="ml-2 w-4 h-4" />
                    </Button>

                    {/* Schedule Dialog */}
                    {isMobile ? (
                        <Drawer open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
                            <DrawerContent>
                                <DrawerHeader>
                                    <DrawerTitle>Select a Schedule</DrawerTitle>
                                </DrawerHeader>
                                <div className="p-4">
                                    <ScheduleList
                                        schedules={filteredSchedules}
                                        scheduleId={scheduleId}
                                        scheduleSearch={scheduleSearch}
                                        setScheduleSearch={setScheduleSearch}
                                        alphaAsc={alphaAsc}
                                        setAlphaAsc={setAlphaAsc}
                                        onSelect={handleScheduleSelect}
                                    />
                                </div>
                            </DrawerContent>
                        </Drawer>
                    ) : (
                        <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
                            <DialogContent className="max-w-lg">
                                <DialogHeader>
                                    <DialogTitle>Select a Schedule</DialogTitle>
                                </DialogHeader>
                                <ScheduleList
                                    schedules={filteredSchedules}
                                    scheduleId={scheduleId}
                                    scheduleSearch={scheduleSearch}
                                    setScheduleSearch={setScheduleSearch}
                                    alphaAsc={alphaAsc}
                                    setAlphaAsc={setAlphaAsc}
                                    onSelect={handleScheduleSelect}
                                />
                            </DialogContent>
                        </Dialog>
                    )}
                </div>

                {/* Schedule Index */}
                <div className="space-y-2">
                    <Label htmlFor="schedule_index">Schedule Index</Label>
                    <Input
                        id="schedule_index"
                        type="number"
                        value={scheduleIndex ?? ""}
                        onChange={(e) => setScheduleIndex(e.target.value ? Number(e.target.value) : null)}
                        placeholder="Schedule index (e.g., 0, 1, 2...)"
                    />
                </div>

                {/* Tracker Selection */}
                <div className="space-y-2">
                    <Label>Tracker</Label>
                    <Select
                        value={trackerId ?? "none"}
                        onValueChange={(value) => setTrackerId(value === "none" ? null : value)}
                    >
                        <SelectTrigger>
                            <SelectValue placeholder="Select a tracker" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="none">No Tracker</SelectItem>
                            {trackers.map((tracker) => (
                                <SelectItem key={tracker.tracker_id} value={tracker.tracker_id}>
                                    {tracker.form?.form_name ??
                                        tracker.tracker_name ??
                                        `Tracker ${tracker.tracker_id.slice(0, 8)}`}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Timing Label */}
                <div className="space-y-2">
                    <Label htmlFor="timing_label">Timing Label</Label>
                    <Input
                        id="timing_label"
                        type="text"
                        value={timingLabel}
                        onChange={(e) => setTimingLabel(e.target.value)}
                        placeholder="e.g., Baseline, Week 1, Month 3"
                    />
                </div>

                {/* Timing Interval */}
                <div className="space-y-2">
                    <Label htmlFor="timing_interval">Timing Interval</Label>
                    <Input
                        id="timing_interval"
                        type="number"
                        value={timingInterval ?? ""}
                        onChange={(e) => setTimingInterval(e.target.value ? Number(e.target.value) : null)}
                        placeholder="Interval value (e.g., 1, 2, 3...)"
                    />
                </div>

                {/* Time Unit */}
                <div className="space-y-2">
                    <Label>Time Unit</Label>
                    <Select
                        value={timeUnit ?? "none"}
                        onValueChange={(value) => setTimeUnit(value === "none" ? null : value)}
                    >
                        <SelectTrigger>
                            <SelectValue placeholder="Select time unit" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="none">No Time Unit</SelectItem>
                            {TIME_UNITS.map((unit) => (
                                <SelectItem key={unit} value={unit}>
                                    {unit.charAt(0).toUpperCase() + unit.slice(1)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Save Button */}
                <fc.SaveButton
                    onClick={handleSave}
                    isLoading={updateEntryMutation.isPending}
                    label="Save Entry Attributes"
                    className="w-full"
                />
            </div>

            {/* Entry Values Section */}
            <div className="space-y-4 bg-white shadow-sm p-6 border rounded-lg">
                <h3 className="font-semibold text-lg">Entry Values</h3>
                <p className="text-slate-500 text-sm">Click on any value to edit it inline.</p>
                {entry.entry_values && entry.entry_values.length > 0 ? (
                    <EditableEntryCardEntryValues size="lg" entryValues={entry.entry_values} diaryId={diary.diary_id} />
                ) : (
                    <p className="text-slate-400 text-sm">No entry values found.</p>
                )}
            </div>

            {/* Danger Zone */}
            <div className="space-y-4 bg-red-50 p-6 border border-red-200 rounded-lg">
                <h3 className="font-semibold text-red-800 text-lg">Danger Zone</h3>
                <p className="text-red-700 text-sm">
                    Deleting this entry will permanently remove all associated entry values. This action cannot be
                    undone.
                </p>
                <fc.DeleteButton
                    onClick={handleDelete}
                    isLoading={deleteEntryMutation.isPending}
                    label="Delete Entry"
                    className="w-auto"
                />
            </div>
        </div>
    );
}
